import type {
  AttractExperimentConfig,
  AttractExperimentVariant,
} from '@/shared/types/api';
import { loopFor } from './schedule';

export interface AttractAssignment {
  experimentKey: string;
  variant: string;
  slot: string;
  videoUrl: string | null;
}

export interface AttractSessionAttribution {
  attract_experiment_key?: string;
  attract_variant?: string;
  attract_slot?: string;
}

export interface LocalSlot {
  localDate: string;
  localHour: number;
  slot: string;
}

export interface UtcSlot {
  utcDate: string;
  utcHour: number;
  slot: string;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function localSlot(now: Date): LocalSlot {
  const localDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const localHour = now.getHours();
  return { localDate, localHour, slot: `${localDate}T${pad(localHour)}` };
}

export function utcSlot(now: Date): UtcSlot {
  const utcDate = now.toISOString().slice(0, 10);
  const utcHour = now.getUTCHours();
  return { utcDate, utcHour, slot: `${utcDate}T${pad(utcHour)}` };
}

export function msUntilNextUtcHour(now: Date): number {
  const next = new Date(now.getTime());
  next.setUTCMinutes(60, 0, 0);
  return Math.max(0, next.getTime() - now.getTime());
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function parseVariant(raw: unknown): AttractExperimentVariant | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const { key, video_url: videoUrl } = raw as Record<string, unknown>;
  if (!isNonEmptyString(key)) {
    return null;
  }
  return { key, video_url: isNonEmptyString(videoUrl) ? videoUrl : null };
}

export function parseAttractExperiment(
  raw: unknown
): AttractExperimentConfig | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const config = raw as Record<string, unknown>;
  const { key, seed, starts_at: startsAt, ends_at: endsAt } = config;
  if (
    !(
      isNonEmptyString(key) &&
      isNonEmptyString(seed) &&
      isNonEmptyString(startsAt) &&
      isNonEmptyString(endsAt)
    ) ||
    Number.isNaN(Date.parse(startsAt)) ||
    Number.isNaN(Date.parse(endsAt)) ||
    !Array.isArray(config.variants) ||
    config.variants.length === 0
  ) {
    return null;
  }
  const variants = config.variants.map(parseVariant);
  if (variants.some((variant) => variant === null)) {
    return null;
  }
  return {
    key,
    seed,
    starts_at: startsAt,
    ends_at: endsAt,
    variants: variants as AttractExperimentVariant[],
  };
}

export function isAttractExperimentRunning(
  config: AttractExperimentConfig,
  now: Date
): boolean {
  const time = now.getTime();
  return (
    Date.parse(config.starts_at) <= time && time < Date.parse(config.ends_at)
  );
}

// The kiosk config is fetched once at boot, so the window is re-checked on
// the device: a kiosk that stays up past ends_at must fall back on its own.
export function resolveAttractAssignment(
  config: AttractExperimentConfig | null,
  unit: string | null | undefined,
  now: Date
): AttractAssignment | null {
  if (!(config && unit && isAttractExperimentRunning(config, now))) {
    return null;
  }
  const { utcDate, utcHour, slot } = utcSlot(now);
  const variant = loopFor(
    config.seed,
    unit,
    utcDate,
    utcHour,
    config.variants.map((entry) => entry.key)
  );
  const videoUrl =
    config.variants.find((entry) => entry.key === variant)?.video_url ?? null;
  return { experimentKey: config.key, variant, slot, videoUrl };
}

export function isSameAssignment(
  left: AttractAssignment | null,
  right: AttractAssignment | null
): boolean {
  if (left === null || right === null) {
    return left === right;
  }
  return (
    left.experimentKey === right.experimentKey &&
    left.variant === right.variant &&
    left.slot === right.slot &&
    left.videoUrl === right.videoUrl
  );
}

export function preloadVideosFor(
  config: AttractExperimentConfig | null,
  assignment: AttractAssignment | null
): AttractExperimentVariant[] {
  if (!(config && assignment)) {
    return [];
  }
  const seen = new Set<string>(
    assignment.videoUrl ? [assignment.videoUrl] : []
  );
  return config.variants.filter((variant) => {
    if (!variant.video_url || seen.has(variant.video_url)) {
      return false;
    }
    seen.add(variant.video_url);
    return true;
  });
}

// Mirrors the backend's NewSessionRequest limits: a value it would reject with
// 422 must not cost the visitor their session, so it is dropped instead.
const MAX_ATTRIBUTION_LENGTH = 64;
const SLOT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}$/;

export function sessionAttributionFor(
  assignment: AttractAssignment | null
): AttractSessionAttribution {
  if (
    !assignment ||
    assignment.experimentKey.length > MAX_ATTRIBUTION_LENGTH ||
    assignment.variant.length > MAX_ATTRIBUTION_LENGTH ||
    !SLOT_PATTERN.test(assignment.slot)
  ) {
    return {};
  }
  return {
    attract_experiment_key: assignment.experimentKey,
    attract_variant: assignment.variant,
    attract_slot: assignment.slot,
  };
}
